/**
 * Web Notifications API — системные уведомления браузера.
 */

import { formatOrderStatusNotificationBody } from 'utils/order-status-notification-text'
import type { OrderStatusChangeItem } from 'utils/order-status-notification-text'
import type { Notification as AppNotification } from 'types/notifications'
import { documentUploadedHref, documentUploadedTitle } from 'types/notifications'
import { ordersListPath } from 'routes/url-constants'
import { isNotificationLeaderTab } from 'utils/notification-tab-leader'

export type { OrderStatusChangeItem }

export const isBrowserNotificationsSupported = (): boolean => typeof window !== 'undefined' && 'Notification' in window

export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isBrowserNotificationsSupported()) return 'unsupported'
  return Notification.permission
}

export const requestNotificationPermission = (): Promise<NotificationPermission> => {
  if (!isBrowserNotificationsSupported()) {
    return Promise.resolve('denied' as NotificationPermission)
  }
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Promise.resolve(Notification.permission)
  }
  return Notification.requestPermission()
}

export interface ShowOrderStatusNotificationParams {
  /** Заявки, у которых изменился статус (номер + текущая резолюция). */
  items: OrderStatusChangeItem[]
  /** Заголовок уведомления (по умолчанию «ОТК»). */
  title?: string
  /** Свой текст вместо автогенерации по резолюциям. */
  body?: string
}

/** Не чаще одного показа за этот интервал (антиспам). */
const LAST_NOTIFICATION_MS = 2500

/**
 * Через сколько мс закрыть уведомление программно.
 * Точную длительность показа задаёт ОС; `requireInteraction` помогает не исчезать мгновенно.
 */
const NOTIFICATION_AUTO_CLOSE_MS = 20000

let lastNotificationAt = 0
let lastChatNotificationAt = 0

const toAbsoluteUrl = (path: string): string => {
  const base = typeof window !== 'undefined' ? window.location.origin : ''
  return `${base}${path}`
}

/**
 * Показывает одно сводное уведомление о смене статуса заявок (throttle).
 */
export const showOrderStatusChangeNotification = ({ items, title = 'ОТК', body: bodyOverride }: ShowOrderStatusNotificationParams): void => {
  if (!isBrowserNotificationsSupported() || Notification.permission !== 'granted') return
  if (!isNotificationLeaderTab()) return
  if (!items.length) return

  const now = Date.now()
  if (now - lastNotificationAt < LAST_NOTIFICATION_MS) return
  lastNotificationAt = now

  const body = bodyOverride ?? formatOrderStatusNotificationBody(items)

  try {
    // Уникальный tag: при одном и том же tag ОС часто *заменяют* уведомление без повторного звука.
    const tag = `otk-order-status-${now}`

    const n = new Notification(title, {
      body,
      icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
      tag,
      // Явно не silent: звук — по настройкам ОС/браузера (на части Linux звука может не быть вообще)
      silent: false,
      // Дольше держим на экране (до `NOTIFICATION_AUTO_CLOSE_MS` или закрытия пользователем); поведение зависит от ОС.
      requireInteraction: true,
    })

    let closeTimer: number | undefined
    const clearCloseTimer = () => {
      if (closeTimer !== undefined) {
        window.clearTimeout(closeTimer)
        closeTimer = undefined
      }
    }

    closeTimer = window.setTimeout(() => {
      closeTimer = undefined
      try {
        n.close()
      } catch {
        // ignore
      }
    }, NOTIFICATION_AUTO_CLOSE_MS)

    n.onclose = () => {
      clearCloseTimer()
    }

    n.onclick = () => {
      clearCloseTimer()
      const item = items[0]
      // Если несколько заявок — ведём на список.
      if (items.length > 1 || !item?.orderNumber) {
        window.focus()
        window.location.assign(toAbsoluteUrl(ordersListPath))
        n.close()
        return
      }

      const { orderNumber } = item
      const path = `/order/${orderNumber}`

      window.focus()
      window.location.assign(toAbsoluteUrl(path))
      n.close()
    }
  } catch {
    // ignore
  }
}

export interface ShowChatNotificationParams {
  items: AppNotification[]
  title?: string
}

const LAST_CHAT_NOTIFICATION_MS = 2500

/**
 * Сводное системное уведомление о новых чат-уведомлениях (socket: `notifications`).
 * Показывает номер заявки и проигрывает тот же сигнал, что и для изменений статуса.
 */
export const showChatNotifications = ({ items, title = 'ОТК' }: ShowChatNotificationParams): void => {
  if (!isBrowserNotificationsSupported() || Notification.permission !== 'granted') return
  if (!isNotificationLeaderTab()) return
  if (!items.length) return

  const now = Date.now()
  if (now - lastChatNotificationAt < LAST_CHAT_NOTIFICATION_MS) return
  lastChatNotificationAt = now

  const uniqueInternal: string[] = []
  const uniqueExternal: string[] = []
  const seen = new Set<string>()

  items.forEach((it) => {
    const { orderId, isInternal } = it
    if (!orderId) return
    if (seen.has(orderId)) return
    seen.add(orderId)
    if (isInternal) uniqueInternal.push(orderId)
    else uniqueExternal.push(orderId)
  })

  const lines: string[] = []
  if (uniqueInternal.length === 1) {
    lines.push(`Внутреннее сообщение по заявке ${uniqueInternal[0]}`)
  } else if (uniqueInternal.length > 1) {
    lines.push(`Внутренние сообщения по заявкам: ${uniqueInternal.join(', ')}`)
  }

  if (uniqueExternal.length === 1) {
    lines.push(`Новое сообщение по заявке ${uniqueExternal[0]}`)
  } else if (uniqueExternal.length > 1) {
    lines.push(`Сообщения по заявкам: ${uniqueExternal.join(', ')}`)
  }

  const body = lines.join('\n')
  if (!body) return

  try {
    const tag = `otk-chat-${now}`
    const n = new Notification(title, {
      body,
      icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
      tag,
      silent: false,
      requireInteraction: true,
    })

    let closeTimer: number | undefined
    const clearCloseTimer = () => {
      if (closeTimer !== undefined) {
        window.clearTimeout(closeTimer)
        closeTimer = undefined
      }
    }

    closeTimer = window.setTimeout(() => {
      closeTimer = undefined
      try {
        n.close()
      } catch {
        // ignore
      }
    }, NOTIFICATION_AUTO_CLOSE_MS)

    n.onclose = () => {
      clearCloseTimer()
    }

    n.onclick = () => {
      clearCloseTimer()
      // Если в уведомлении несколько заявок — ведём на список.
      if (seen.size > 1) {
        window.focus()
        window.location.assign(toAbsoluteUrl(ordersListPath))
        n.close()
        return
      }

      const first = items[0]
      const orderId = first?.orderId
      if (orderId) {
        window.focus()
        window.location.assign(toAbsoluteUrl(`/order/${orderId}/chat`))
        n.close()
        return
      }

      window.focus()
      n.close()
    }
  } catch {
    // ignore
  }
}

export interface ShowDocumentUploadedNotificationParams {
  items: AppNotification[]
  title?: string
}

/**
 * Системное уведомление: клиент прикрепил документы (одно OS-notification на id).
 * Клик → карточка заявки `/order/{orderId}`. Без throttle по orderId — два файла = два toast.
 */
export const showDocumentUploadedNotifications = ({ items, title = 'ОТК' }: ShowDocumentUploadedNotificationParams): void => {
  if (!isBrowserNotificationsSupported() || Notification.permission !== 'granted') return
  if (!isNotificationLeaderTab()) return
  if (!items.length) return

  items.forEach((it) => {
    if (!it.orderId) return
    const body = documentUploadedTitle(it.orderId)
    const detail = it.fileName ? `${body}\n${it.fileName}` : body
    const tag = `otk-document-${it.id || `${it.orderId}-${it.createdAt}`}`

    try {
      const n = new Notification(title, {
        body: detail,
        icon: `${process.env.PUBLIC_URL || ''}/favicon.ico`,
        tag,
        silent: false,
        requireInteraction: true,
      })

      let closeTimer: number | undefined
      const clearCloseTimer = () => {
        if (closeTimer !== undefined) {
          window.clearTimeout(closeTimer)
          closeTimer = undefined
        }
      }

      closeTimer = window.setTimeout(() => {
        closeTimer = undefined
        try {
          n.close()
        } catch {
          // ignore
        }
      }, NOTIFICATION_AUTO_CLOSE_MS)

      n.onclose = () => {
        clearCloseTimer()
      }

      n.onclick = () => {
        clearCloseTimer()
        window.focus()
        window.location.assign(toAbsoluteUrl(documentUploadedHref(it.orderId)))
        n.close()
      }
    } catch {
      // ignore
    }
  })
}
